import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  CheckCircle, ArrowRight, Crown, MagnifyingGlass,
  Broadcast, ListChecks, Scales, Users, Sparkle,
} from "@phosphor-icons/react";
import { Button } from "../components/ui/button";
import Breadcrumb from "../components/Breadcrumb";
import { breadcrumbSchema, productOfferSchema, faqPageSchema } from "../lib/jsonLd";
import { useAuth } from "../lib/AuthContext";
import { toast } from "sonner";

// Same-origin URL — production (npilookup.com) proxies /api/* to the backend.
// Using window.location.origin avoids cross-origin CORS issues.
const API_BASE = typeof window !== "undefined" ? window.location.origin : (process.env.REACT_APP_BACKEND_URL || "");

const PLANS = [
  {
    key: "free",
    name: "Free",
    price: "$0",
    cadence: "forever",
    subtitle: "For trying lookup, monitoring, and reconciliation on a small provider list",
    cta: "Start free",
    tone: "neutral",
    highlights: [
      "100 NPI lookups per day",
      "2 bulk screenings per day",
      "25 NPIs per screening",
      "Monitor up to 25 providers",
      "Reconciliation included",
      "Limited exports",
    ],
    specs: {
      lookups: "100 / day",
      bulk_runs: "2 / day",
      bulk_size: "25 NPIs",
      monitoring: "25 providers",
      reconcile: true,
      seats: "1 user",
    },
  },
  {
    key: "pro",
    name: "Pro",
    price: "$49",
    annualPrice: "$490",
    annualMonthlyEquiv: "$40.83",
    cadence: "per month",
    subtitle: "For solo billers and small healthcare teams",
    cta: "Upgrade to Pro",
    tone: "teal",
    seat_addon: "+$15 / user / month",
    highlights: [
      "300 NPI lookups per day",
      "Monitor up to 250 providers",
      "5 bulk screenings per day",
      "100 NPIs per screening",
      "Reconciliation included",
      "CSV exports",
      "Email alerts",
    ],
    specs: {
      lookups: "300 / day",
      bulk_runs: "5 / day",
      bulk_size: "100 NPIs",
      monitoring: "250 providers",
      reconcile: true,
      seats: "1 user",
    },
  },
  {
    key: "team",
    name: "Team",
    price: "$149",
    annualPrice: "$1,490",
    annualMonthlyEquiv: "$124.17",
    cadence: "per month",
    subtitle: "For billing companies and credentialing teams",
    cta: "Upgrade to Team",
    tone: "navy",
    popular: true,
    seat_addon: "+$20 / user / month",
    highlights: [
      "1,000 NPI lookups per day",
      "Monitor up to 2,500 providers",
      "12 bulk screenings per day",
      "500 NPIs per screening",
      "Shared watchlists",
      "Audit-ready reports",
      "3 included users",
    ],
    specs: {
      lookups: "1,000 / day",
      bulk_runs: "12 / day",
      bulk_size: "500 NPIs",
      monitoring: "2,500 providers",
      reconcile: true,
      seats: "3 users",
    },
  },
  {
    key: "business",
    name: "Business",
    price: "$399",
    annualPrice: "$3,990",
    annualMonthlyEquiv: "$332.50",
    cadence: "per month",
    subtitle: "For staffing firms, MSOs, and larger provider operations",
    cta: "Upgrade to Business",
    tone: "amber",
    seat_addon: "+$25 / user / month",
    highlights: [
      "5,000 NPI lookups per day",
      "Monitor up to 10,000 providers",
      "25 bulk screenings per day",
      "2,500 NPIs per screening",
      "Advanced reconciliation workflows",
      "API access",
      "Role-based access",
      "5 included users",
    ],
    specs: {
      lookups: "5,000 / day",
      bulk_runs: "25 / day",
      bulk_size: "2,500 NPIs",
      monitoring: "10,000 providers",
      reconcile: true,
      seats: "5 users",
    },
  },
  {
    key: "enterprise",
    name: "Enterprise",
    price: "Custom",
    cadence: "",
    subtitle: "For payers, large networks, and custom deployments",
    cta: "Contact sales",
    tone: "dark",
    seat_addon: "Custom seats",
    highlights: [
      "Custom provider monitoring",
      "High-volume bulk screening",
      "Custom alert rules",
      "SSO / SAML",
      "Custom integrations",
      "Dedicated onboarding",
      "SLA support",
    ],
    specs: {
      lookups: "Custom",
      bulk_runs: "Custom",
      bulk_size: "Custom",
      monitoring: "Custom",
      reconcile: true,
      seats: "Custom",
    },
  },
];

const TONE_MAP = {
  neutral: { card: "border-slate-200", accent: "text-slate-900", btn: "bg-slate-900 hover:bg-slate-800 text-white", chip: "bg-slate-100 text-slate-700" },
  teal:    { card: "border-teal/30", accent: "text-teal", btn: "bg-teal hover:bg-teal/90 text-white", chip: "bg-teal/10 text-teal" },
  navy:    { card: "border-navy ring-4 ring-navy/10", accent: "text-navy", btn: "bg-navy hover:bg-navy/90 text-white", chip: "bg-navy/10 text-navy" },
  amber:   { card: "border-amber-300", accent: "text-amber-700", btn: "bg-amber-600 hover:bg-amber-700 text-white", chip: "bg-amber-100 text-amber-700" },
  dark:    { card: "border-slate-800 bg-slate-900 text-slate-100", accent: "text-white", btn: "bg-white hover:bg-slate-200 text-slate-900", chip: "bg-white/10 text-white" },
};

const PricingPage = () => {
  const { isAuthenticated, login, authFetch } = useAuth();
  const [compareOpen, setCompareOpen] = useState(false);
  const [billingInterval, setBillingInterval] = useState("monthly"); // monthly | annual
  const [checkoutLoading, setCheckoutLoading] = useState(null); // plan key currently processing

  const handleCta = async (plan) => {
    if (plan.key === "free") {
      if (!isAuthenticated) { login(); return; }
      window.location.href = "/dashboard";
      return;
    }
    if (plan.key === "enterprise") {
      window.location.href = "mailto:support@npilookup.com?subject=Enterprise%20Plan%20Inquiry";
      return;
    }
    if (!isAuthenticated) { login(); return; }

    setCheckoutLoading(plan.key);
    try {
      const res = await authFetch(`${API_BASE}/api/billing/checkout`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          plan: plan.key,
          interval: billingInterval,
          seat_quantity: 0,
          origin_url: window.location.origin,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        toast.error(data.detail || "Could not start checkout. Please try again.");
        setCheckoutLoading(null);
        return;
      }
      window.location.href = data.url;
    } catch (e) {
      toast.error("Checkout error. Please try again.");
      setCheckoutLoading(null);
    }
  };

  return (
    <>
      <Helmet>
        <title>Pricing — NPI Lookup, Monitoring & Reconciliation Plans</title>
        <meta name="description" content="NPI Lookup pricing for credentialing teams, staffing firms, and MSOs. Free plan includes 100 NPI lookups/day, bulk screening, monitoring, and reconciliation. Paid plans from $49/month scale to unlimited monitoring and API access." />
        <link rel="canonical" href="https://npilookup.com/pricing" />
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema([{ name: "Home", url: "/" }, { name: "Pricing" }]))}</script>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "OfferCatalog",
          name: "NPI Lookup Subscription Plans",
          url: "https://npilookup.com/pricing",
          itemListElement: [
            { priceUsd: 0,   name: "Free",     description: "Try every workflow on a small provider list. 100 lookups/day, 2 bulk runs, monitor up to 25 providers." },
            { priceUsd: 49,  name: "Pro",      description: "For solo billers and small healthcare teams. 300 lookups/day, monitor 250 providers, CSV exports, email alerts." },
            { priceUsd: 149, name: "Team",     description: "For billing companies and credentialing teams. 1,000 lookups/day, monitor 2,500 providers, shared watchlists, audit-ready reports, 3 seats." },
            { priceUsd: 399, name: "Business", description: "For staffing firms, MSOs, and larger provider operations. 5,000 lookups/day, monitor 10,000 providers, API access, role-based access, 5 seats." },
          ].map((t) => productOfferSchema({
            name: `NPI Lookup ${t.name}`,
            description: t.description,
            url: "/pricing",
            priceUsd: t.priceUsd,
            interval: "month",
          })),
        })}</script>
        <script type="application/ld+json">{JSON.stringify(faqPageSchema([
          { q: "Is there a free plan?", a: "Yes. The Free plan includes 100 NPI lookups per day, 2 bulk screenings per day (up to 25 NPIs each), monitoring for up to 25 providers, and roster reconciliation. No credit card required." },
          { q: "Can I cancel any time?", a: "Yes. Cancel or change plans any time from the Stripe billing portal — no contracts, no cancellation fees, and your plan stays active until the end of the current billing period." },
          { q: "Do you offer annual billing?", a: "Yes. Annual plans save ~17% — two months free compared to paying monthly." },
          { q: "What payment methods do you accept?", a: "All major credit and debit cards via Stripe. Business ACH is available on Team and Business plans by request." },
        ]))}</script>
      </Helmet>

      {/* Hero */}
      <section className="bg-gradient-to-b from-white to-slate-50 pt-16 pb-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Breadcrumb items={[{ name: "Home", to: "/" }, { name: "Pricing" }]} className="mb-4" />
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 bg-navy/5 text-navy px-3 py-1 rounded-full text-xs font-semibold tracking-wide uppercase mb-4">
            <Crown className="w-3.5 h-3.5 text-amber-500" weight="fill" />
            4 workflows, 1 operating platform
          </div>
          <h1 className="font-display font-bold text-4xl sm:text-5xl text-slate-900 tracking-tight mb-4">
            Pricing that grows with your roster
          </h1>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto leading-relaxed">
            Lookup, bulk screening, monitoring, and reconciliation — on every plan. Monitoring list size is what moves you up.
          </p>

          {/* Workflow chips */}
          <div className="mt-8 flex flex-wrap justify-center gap-2">
            {[
              { Icon: MagnifyingGlass, label: "Lookup" },
              { Icon: ListChecks, label: "Bulk Screening" },
              { Icon: Broadcast, label: "Monitoring" },
              { Icon: Scales, label: "Reconciliation" },
            ].map(({ Icon, label }) => (
              <div key={label} className="inline-flex items-center gap-2 px-3 py-1.5 bg-white border border-slate-200 rounded-full text-sm text-slate-700">
                <Icon className="w-4 h-4 text-teal" weight="fill" /> {label}
              </div>
            ))}
          </div>

          {/* Monthly / Annual billing toggle */}
          <div className="mt-8 inline-flex items-center gap-1 bg-white border border-slate-200 rounded-full p-1" data-testid="billing-interval-toggle">
            <button
              onClick={() => setBillingInterval("monthly")}
              data-testid="billing-interval-monthly"
              className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${billingInterval === "monthly" ? "bg-slate-900 text-white" : "text-slate-600 hover:text-slate-900"}`}
            >
              Monthly
            </button>
            <button
              onClick={() => setBillingInterval("annual")}
              data-testid="billing-interval-annual"
              className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors inline-flex items-center gap-1.5 ${billingInterval === "annual" ? "bg-slate-900 text-white" : "text-slate-600 hover:text-slate-900"}`}
            >
              Annual
              <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-bold ${billingInterval === "annual" ? "bg-teal-400 text-slate-900" : "bg-teal/10 text-teal"}`}>
                2 months free
              </span>
            </button>
          </div>
        </div>
      </section>

      {/* Plan cards */}
      <section className="pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            {PLANS.map((plan) => {
              const tone = TONE_MAP[plan.tone] || TONE_MAP.neutral;
              const isDark = plan.tone === "dark";
              return (
                <div
                  key={plan.key}
                  data-testid={`plan-card-${plan.key}`}
                  className={`relative rounded-2xl border-2 ${tone.card} ${isDark ? "" : "bg-white"} p-5 flex flex-col transition-all hover:-translate-y-1 hover:shadow-lg`}
                >
                  {plan.popular && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-2.5 py-1 bg-navy text-white text-[10px] font-bold uppercase tracking-wider rounded-full flex items-center gap-1">
                      <Sparkle className="w-3 h-3" weight="fill" /> Most popular
                    </div>
                  )}

                  <div className="mb-4">
                    <div className={`inline-block text-xs font-bold uppercase tracking-wider ${isDark ? "text-white/60" : "text-slate-400"} mb-1`}>
                      {plan.name}
                    </div>
                    <p className={`text-[13px] ${isDark ? "text-white/70" : "text-slate-500"} leading-snug`}>
                      {plan.subtitle}
                    </p>
                  </div>

                  <div className="mb-5">
                    <div className="flex items-baseline gap-1">
                      <span className={`text-3xl font-display font-bold ${tone.accent}`}>
                        {billingInterval === "annual" && plan.annualMonthlyEquiv
                          ? plan.annualMonthlyEquiv
                          : plan.price}
                      </span>
                      {plan.cadence && (
                        <span className={`text-xs ${isDark ? "text-white/60" : "text-slate-500"}`}>{plan.cadence}</span>
                      )}
                    </div>
                    {billingInterval === "annual" && plan.annualPrice && (
                      <div className={`text-[11px] mt-0.5 ${isDark ? "text-white/60" : "text-teal font-medium"}`}>
                        {plan.annualPrice} billed annually
                      </div>
                    )}
                    {plan.seat_addon && (
                      <div className={`text-[11px] mt-1 ${isDark ? "text-white/50" : "text-slate-400"}`}>
                        Seats: {plan.seat_addon}
                      </div>
                    )}
                  </div>

                  <ul className="space-y-2 mb-6 flex-1">
                    {plan.highlights.map((h) => (
                      <li key={h} className={`flex items-start gap-2 text-[13px] ${isDark ? "text-white/80" : "text-slate-700"}`}>
                        <CheckCircle className={`w-4 h-4 shrink-0 mt-0.5 ${isDark ? "text-teal-400" : "text-teal"}`} weight="fill" />
                        <span>{h}</span>
                      </li>
                    ))}
                  </ul>

                  <Button
                    onClick={() => handleCta(plan)}
                    className={`w-full rounded-xl ${tone.btn} font-semibold`}
                    data-testid={`plan-cta-${plan.key}`}
                    disabled={checkoutLoading === plan.key}
                  >
                    {checkoutLoading === plan.key ? "Redirecting…" : plan.cta}
                    {checkoutLoading !== plan.key && <ArrowRight className="w-4 h-4 ml-1" />}
                  </Button>
                </div>
              );
            })}
          </div>

          {/* Compare all features */}
          <div className="mt-10 text-center">
            <button
              onClick={() => setCompareOpen((o) => !o)}
              className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-navy font-medium"
              data-testid="compare-toggle"
            >
              {compareOpen ? "Hide" : "Compare"} limits across plans
              <ArrowRight className={`w-4 h-4 transition-transform ${compareOpen ? "rotate-90" : ""}`} />
            </button>
          </div>

          {compareOpen && (
            <div className="mt-8 bg-white rounded-2xl border border-slate-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="plans-compare-table">
                  <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
                    <tr>
                      <th className="text-left px-4 py-3 font-semibold"></th>
                      {PLANS.map((p) => (
                        <th key={p.key} className="text-left px-4 py-3 font-semibold">{p.name}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {[
                      ["NPI lookups", "lookups", MagnifyingGlass],
                      ["Bulk screenings / day", "bulk_runs", ListChecks],
                      ["Max NPIs per screening", "bulk_size", ListChecks],
                      ["Monitoring list size", "monitoring", Broadcast],
                      ["Reconciliation", "reconcile", Scales],
                      ["Included users", "seats", Users],
                    ].map(([label, field, Icon]) => (
                      <tr key={field} className="hover:bg-slate-50/50">
                        <td className="px-4 py-3 font-medium text-slate-700 flex items-center gap-2">
                          <Icon className="w-4 h-4 text-slate-400" /> {label}
                        </td>
                        {PLANS.map((p) => (
                          <td key={p.key} className="px-4 py-3 text-slate-600">
                            {field === "reconcile"
                              ? (p.specs.reconcile ? <CheckCircle className="w-4 h-4 text-teal" weight="fill" /> : "—")
                              : p.specs[field]}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* FAQ strip */}
      <section className="bg-slate-50 py-14">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display font-bold text-2xl text-slate-900 text-center mb-6">Common questions</h2>
          <div className="space-y-4">
            {[
              { q: "Is reconciliation really on every plan?", a: "Yes. Roster reconciliation is included on Free and every paid plan — we don't meter it by runs. The thing that scales with plan is your monitoring list size." },
              { q: "What's 'limited exports' on Free?", a: "Free accounts can view and use all results on-screen, but CSV/PDF exports are limited. Pro and above include full exports." },
              { q: "Can I add more team members?", a: "Yes. Pro adds seats at $15/user/month, Team at $20, Business at $25. Enterprise seats are custom. Included seats are listed per plan." },
              { q: "What happens if I hit a daily limit?", a: "You'll see a clear message telling you which limit you hit and what plan unlocks more. Your data is never deleted." },
              { q: "Is there a free trial of Pro/Team/Business?", a: "The Free plan itself is the trial — it includes every workflow with real (smaller) limits so you can validate the fit before upgrading." },
            ].map((f, i) => (
              <details key={i} className="bg-white rounded-xl border border-slate-200 p-4 group">
                <summary className="font-medium text-slate-900 cursor-pointer list-none flex items-center justify-between">
                  {f.q}
                  <ArrowRight className="w-4 h-4 text-slate-400 group-open:rotate-90 transition-transform" />
                </summary>
                <p className="mt-3 text-sm text-slate-600 leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Enterprise band */}
      <section className="bg-slate-900 text-white py-14">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h3 className="font-display font-bold text-2xl mb-3">Running provider ops at scale?</h3>
          <p className="text-slate-300 mb-6 max-w-xl mx-auto">
            Payers, health systems, and networks running 10,000+ NPIs get custom limits, SSO/SAML, dedicated onboarding, and SLA support.
          </p>
          <a
            href="mailto:support@npilookup.com?subject=Enterprise%20Plan%20Inquiry"
            className="inline-flex items-center gap-2 bg-white text-slate-900 font-semibold px-6 py-3 rounded-xl hover:bg-slate-100 transition-colors"
            data-testid="enterprise-contact-cta"
          >
            Talk to sales <ArrowRight className="w-4 h-4" />
          </a>
        </div>
      </section>
    </>
  );
};

export default PricingPage;
